/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { AutoCompleteModule } from "primeng/autocomplete";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { Subject, takeUntil } from "rxjs";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";

/**
 * Customer selector component with autocomplete search
 * Used in scheduled order create/edit forms to select a customer
 */
@Component({
  selector: "app-customer-selector",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    CardModule,
    TagModule,
  ],
  templateUrl: "./customer-selector.component.html",
  styleUrl: "./customer-selector.component.scss",
})
export class CustomerSelectorComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // FormControl for the autocomplete field
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customerControl = new FormControl<any>(null);

  // Getter/setter to ensure displayLabel always exists and sync with FormControl
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input()
  get selectedCustomer(): any {
    return this.customerControl.value;
  }
  set selectedCustomer(value: any) {
    const customerWithLabel = this.ensureDisplayLabel(value);
    this.customerControl.setValue(customerWithLabel, { emitEvent: false });

    // Update display name for edit mode
    if (value) {
      this.customerDisplayName = this.getCustomerLabel(value);
    }

    // Disable control in edit mode
    if (this.isEditMode) {
      this.customerControl.disable({ emitEvent: false });
    }
  }

  @Input() siteId!: number;
  @Input() disabled = false;
  @Input() required = false;
  @Input() isEditMode = false; // Read-only mode in edit (customer locked)
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() customerChange = new EventEmitter<any>(); // TODO: Replace with Customer interface

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredCustomers: any[] = []; // TODO: Replace with Customer[]
  isLoading = false;
  customerDisplayName: string = ""; // For edit mode display

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
  ) {}

  /**
   * Ensure a customer object has the displayLabel property
   * @param customer Customer object
   * @returns Customer with displayLabel
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ensureDisplayLabel(customer: any): any {
    if (!customer) return customer;
    // If it already has displayLabel, return as-is
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (customer.displayLabel) return customer;
    // Otherwise, add displayLabel
    return {
      ...customer,
      displayLabel: this.getCustomerLabel(customer),
    };
  }

  /**
   * Handle focus event on autocomplete to fetch all customers
   * Triggered when user clicks on the dropdown or field
   */
  onCustomerFocus(): void {
    // If field is empty and no customers loaded yet, fetch all customers
    if (!this.selectedCustomer && this.filteredCustomers.length === 0) {
      this.search({ query: "" });
    }
  }

  /**
   * Search for customers based on user input
   * @param event AutoComplete search event
   */
  search(event: { query: string }): void {
    const query = event.query;

    // When query is empty (dropdown click), fetch all customers from page 1
    // When typing, require at least 2 characters for search
    if (query.length > 0 && query.length < 2) {
      this.filteredCustomers = [];
      return;
    }

    this.isLoading = true;

    const token = this.accessToken ?? this.sessionService.accessToken;
    if (!token) {
      this.isLoading = false;
      return;
    }

    this.qpilotService
      .getCustomersForSite(
        this.siteId,
        token,
        1,
        query || undefined, // Pass undefined for empty query to fetch all customers
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customers) => {
          // Add displayLabel property for PrimeNG AutoComplete
          this.filteredCustomers = customers.map((customer) => ({
            ...customer,
            displayLabel: this.getCustomerLabel(customer),
          }));
          this.isLoading = false;
        },
        error: () => {
          this.filteredCustomers = [];
          this.isLoading = false;
        },
      });
  }

  /**
   * Handle customer selection
   * @param customer Selected customer object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  onSelect(customer: any): void {
    // Setter will automatically ensure displayLabel exists
    this.customerChange.emit(this.selectedCustomer);
  }

  /**
   * Handle customer clear
   */
  onClear(): void {
    this.customerControl.setValue(null);
    this.customerChange.emit(undefined);
  }

  /**
   * Get display label for a customer in the dropdown
   * @param customer Customer object
   * @returns Formatted display string
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCustomerLabel(customer: any): string {
    if (!customer) return "";

    const parts: string[] = [];

    // Add name
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (customer.FirstName || customer.LastName) {
      parts.push(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        `${customer.FirstName || ""} ${customer.LastName || ""}`.trim(),
      );
    }

    // Add email
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (customer.Email) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parts.push(`(${customer.Email})`);
    }

    // Add customer ID
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (customer.Id) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parts.push(`#${customer.Id}`);
    }

    return parts.join(" ");
  }

  /**
   * Get customer initials for avatar display
   * @returns Initials string (e.g., "DB")
   */
  getCustomerInitials(): string {
    if (!this.selectedCustomer) return "";

    const parts: string[] = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.selectedCustomer.FirstName) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      parts.push(this.selectedCustomer.FirstName.charAt(0).toUpperCase());
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.selectedCustomer.LastName) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      parts.push(this.selectedCustomer.LastName.charAt(0).toUpperCase());
    }

    return parts.join("");
  }

  /**
   * Get location string from customer billing info
   * @returns Location string (e.g., "San Francisco, CA")
   */
  getLocationString(): string {
    if (!this.selectedCustomer) return "";

    const parts: string[] = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.selectedCustomer.BillingCity) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parts.push(this.selectedCustomer.BillingCity);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.selectedCustomer.BillingState) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      parts.push(this.selectedCustomer.BillingState);
    }

    return parts.join(", ");
  }

  /**
   * Format phone number for display
   * @param phone Phone number string
   * @returns Formatted phone number
   */
  formatPhoneNumber(phone: string): string {
    if (!phone) return "";

    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX for 10-digit US numbers
    if (cleaned.length === 10) {
      return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    }

    // Format as +X (XXX) XXX-XXXX for 11-digit numbers (with country code)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
    }

    // Return original if format is unrecognized
    return phone;
  }

  /**
   * Format currency value for display
   * @param value Numeric value
   * @returns Formatted currency string
   */
  formatCurrency(value: number): string {
    if (value === undefined || value === null) return "";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Format date for display
   * @param dateString Date string
   * @returns Formatted date string
   */
  formatDate(dateString: string): string {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(date);
    } catch {
      return dateString;
    }
  }

  /**
   * Check if customer has contact information
   * @returns True if has email or phone
   */
  hasContactInfo(): boolean {
    if (!this.selectedCustomer) return false;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return !!(this.selectedCustomer.Email || this.selectedCustomer.PhoneNumber);
  }

  /**
   * Check if customer has address information
   * @returns True if has address fields
   */
  hasAddressInfo(): boolean {
    if (!this.selectedCustomer) return false;
    return !!(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (
        this.selectedCustomer.BillingStreet1 ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedCustomer.BillingCity ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedCustomer.BillingState
      )
    );
  }

  /**
   * Check if customer has stats information
   * @returns True if has orders, lifetime value, or created date
   */
  hasStatsInfo(): boolean {
    if (!this.selectedCustomer) return false;
    return !!(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (
        this.selectedCustomer.TotalOrders !== undefined ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedCustomer.LifetimeValue ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedCustomer.CreatedUtc ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedCustomer.CreatedAt ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.selectedCustomer.Created
      )
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
